import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { Drawer } from 'ui-components/components/drawer/drawer';
import { DrawerHeader } from 'ui-components/components/drawer/drawer-header';
import { FlexRow } from 'ui-components/components/flex/row';
import { Gap } from 'ui-components/components/flex/gap';
import { Text } from 'ui-components/components/text';
import { TextType } from 'ui-components/components/text/text-type';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { GuidService } from 'ui-components/services/guid-service';
import { NotificationService, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';

import type { WorkflowRulePayload } from '../../../api/automation-workflow-types';
import { convertToApiFilters, convertToApiAction } from '../step-editor/to-api';
import { getFilterRegistry, getActionRegistry } from '../../registries';

import { AUTOMATION_WORKFLOW_DRAWER_CANCELLED } from '../../../../../constants/analytics-events';
import { Segment } from '../../../../../services/analytics-services/segment';
import { LogService } from '../../../../../services/log-service';
import { createWorkflowRule, updateWorkflowRule } from '../../../api/automation-workflow.api';
import type { Workflow, WorkflowFormData, WorkflowStep, WorkflowEntityType } from '../../types.d';
import { WorkflowCanvas } from '../workflow-canvas';
import { WorkflowDetailsPanel } from '../workflow-details-panel';
import { messages } from './messages';
import styles from './styles.module.less';

type WorkflowDrawerProps = {
  workflow: Workflow | null;
  allWorkflows: Workflow[];
  onSave: (formData: WorkflowFormData) => void;
  onDelete: (id: string) => void;
  onClose: () => void;
};

export const WorkflowDrawer: React.FC<WorkflowDrawerProps> = ({ workflow, allWorkflows, onSave, onDelete, onClose }) => {
  const intl = useIntl();
  const isEditMode = workflow !== null;
  const [name, setName] = useState(workflow?.name ?? '');
  const [description, setDescription] = useState(workflow?.description ?? '');

  const initialSteps: WorkflowStep[] = useMemo(() => {
    if (workflow?.steps && workflow.steps.length > 0) {
      return workflow.steps;
    }
    return [
      {
        id: GuidService.newGuid(),
        type: 'trigger',
        title: intl.formatMessage(messages.defaultTriggerTitle),
        eventType: undefined,
        isConfigured: false,
        eventFilters: [],
      },
      {
        id: GuidService.newGuid(),
        type: 'action',
        title: '',
        description: '',
        isConfigured: false,
      },
    ];
  }, [workflow?.steps, intl]);

  const [steps, setSteps] = useState<WorkflowStep[]>(initialSteps);
  const initialSelectedStepId = !isEditMode ? (initialSteps.find((s) => s.type === 'action')?.id ?? null) : null;
  const [selectedStepId, setSelectedStepId] = useState<string | null>(initialSelectedStepId);
  const [entityType, setEntityType] = useState<WorkflowEntityType>(workflow?.entityType ?? 'Order');

  const stepsRef = useRef(steps);
  stepsRef.current = steps;
  const prevSelectedStepIdRef = useRef<string | null>(selectedStepId);

  // Tracks stepId -> apiRuleId synchronously, avoiding stale-closure issues with async creates
  const ruleIdMapRef = useRef<Map<string, number>>(
    new Map(initialSteps.flatMap((s) => (s.apiRuleId !== undefined ? [[s.id, s.apiRuleId]] : []))),
  );

  // Guards against duplicate inflight createWorkflowRule calls for the same step.
  // If a create fails the step has no apiRuleId, so the next deselect would fire another create.
  // Tracking in-progress step IDs prevents that until the first attempt completes.
  const creatingStepIdsRef = useRef<Set<string>>(new Set());

  const handleUpdateStep = useCallback((updatedStep: WorkflowStep) => {
    setSteps((prevSteps) => prevSteps.map((step) => (step.id === updatedStep.id ? updatedStep : step)));
  }, []);

  const handleStepsChange = useCallback((newSteps: WorkflowStep[]) => {
    setSteps(newSteps);
  }, []);

  const handleRuleSaveError = useCallback(
    (err: unknown, action: 'create' | 'update') => {
      LogService.logError(new Error(`Failed to ${action} workflow rule`, { cause: err }));
      NotificationService.showNotification({
        content: intl.formatMessage(messages.saveRuleError),
        notificationType: NOTIFICATION_TYPE_ERROR,
      });
    },
    [intl],
  );

  const handleStepBlur = useCallback(
    (step: WorkflowStep) => {
      const payload: WorkflowRulePayload = {
        automationWorkflowRuleName: step.title,
        description: step.description ?? '',
        entityType,
        filters: convertToApiFilters(step.filters ?? [], getFilterRegistry(entityType)),
        actions: (step.actions ?? []).map((action) => convertToApiAction(action, getActionRegistry(entityType))),
      };

      const apiRuleId = ruleIdMapRef.current.get(step.id) ?? step.apiRuleId;

      if (apiRuleId !== undefined) {
        return updateWorkflowRule(apiRuleId, payload).catch((err) => handleRuleSaveError(err, 'update'));
      }

      if (creatingStepIdsRef.current.has(step.id)) {
        return Promise.resolve();
      }

      creatingStepIdsRef.current.add(step.id);
      return createWorkflowRule(payload)
        .then((rule) => {
          ruleIdMapRef.current.set(step.id, rule.automationWorkflowRuleId);
          handleUpdateStep({ ...step, apiRuleId: rule.automationWorkflowRuleId });
        })
        .catch((err) => handleRuleSaveError(err, 'create'))
        .finally(() => {
          creatingStepIdsRef.current.delete(step.id);
        });
    },
    [entityType, handleUpdateStep, handleRuleSaveError],
  );

  // Save a rule step whenever it is deselected. Using a useEffect instead of calling
  // handleStepBlur directly in the blur handler ensures that any input components with
  // internal local state (e.g. TextAreaInput) have had time to flush their onChange
  // before we read the committed step from stepsRef.
  useEffect(() => {
    const prevId = prevSelectedStepIdRef.current;
    prevSelectedStepIdRef.current = selectedStepId;

    if (prevId === null || prevId === selectedStepId) {
      return;
    }

    const prevStep = stepsRef.current.find((s) => s.id === prevId);

    if (prevStep?.type === 'action') {
      handleStepBlur(prevStep);
    }
  }, [selectedStepId, handleStepBlur]);

  const isNameValid = name.trim().length > 0;
  const showNameError = !isNameValid && name.length > 0;

  const handleSave = useCallback(() => {
    if (!isNameValid) {
      return;
    }

    onSave({
      name: name.trim(),
      description: description.trim(),
      isActive: workflow?.isActive ?? true,
      steps,
      entityType,
    });

    onClose();
  }, [isNameValid, name, description, workflow?.isActive, steps, entityType, onSave, onClose]);

  const handleCancel = useCallback(() => {
    Segment.trackEvent(AUTOMATION_WORKFLOW_DRAWER_CANCELLED, {
      mode: isEditMode ? 'edit' : 'create',
      workflowId: workflow?.id,
      workflowStatus: workflow?.automationWorkflowStatus,
    });
    onClose();
  }, [isEditMode, workflow, onClose]);

  const handleCanvasClick = useCallback((e: React.MouseEvent) => {
    if (!(e.target as Element).closest('[data-step-card]')) {
      setSelectedStepId(null);
    }
  }, []);

  const displayName = name.trim().length > 0 ? name.trim() : intl.formatMessage(messages.untitledWorkflow);

  return (
    <Drawer onCancel={handleCancel} onClose={handleCancel}>
      <DrawerHeader
        className={styles['drawer-header']}
        closeButtonAriaLabel={messages.closeDrawer}
        title={
          <FlexRow gap={Gap.Space12}>
            <Text textType={TextType.H3} value={displayName} />
          </FlexRow>
        }
      />

      <FlexRow className={styles['drawer-body']} gap={Gap.Space32}>
        <WorkflowDetailsPanel
          description={description}
          name={name}
          onDescriptionChange={setDescription}
          onNameChange={setName}
          showNameError={showNameError}
        />
        <div className={styles['workflow-canvas']} onClick={handleCanvasClick}>
          <WorkflowCanvas
            allWorkflows={allWorkflows}
            currentWorkflowId={workflow?.id ?? null}
            entityType={entityType}
            onEntityTypeChange={setEntityType}
            onSelectStep={setSelectedStepId}
            onStepsChange={handleStepsChange}
            onUpdateStep={handleUpdateStep}
            selectedStepId={selectedStepId}
            steps={steps}
          />
        </div>
      </FlexRow>

      <div className={styles['drawer-footer']}>
        <Button onClick={handleCancel} theme={THEME_CONSTANTS.secondary}>
          <Text textType={TextType.Caption} value={messages.cancelButton} />
        </Button>
        <FlexRow gap={Gap.Space12}>
          {isEditMode && (
            <Button className={styles['delete-button']} onClick={() => onDelete(workflow!.id)} theme={THEME_CONSTANTS.link}>
              <Text textType={TextType.Caption} value={messages.deleteButton} />
            </Button>
          )}
          <Button disabled={!isNameValid} onClick={handleSave} theme={THEME_CONSTANTS.primary}>
            <Text textType={TextType.Caption} value={messages.saveButton} />
          </Button>
        </FlexRow>
      </div>
    </Drawer>
  );
};
